import React from "react";
import { useCachedImage } from "../hooks/useCachedImage";
import { XIcon } from "./icons/FeatherIcons";
import { type MessageReaction, type User } from "../types";
import { toJsDate } from "../utils/toJsDate";

interface ReactionDetailsModalProps {
  emoji: string;
  reactions: MessageReaction[];
  participants: User[];
  onClose: () => void;
}

const ReactionDetailsModal: React.FC<ReactionDetailsModalProps> = ({
  emoji,
  reactions,
  participants,
  onClose,
}) => {
  // Filter reactions for this specific emoji
  const emojiReactions = reactions.filter((r) => r.emoji === emoji);

  // Get user details for each reaction
  const reactionsWithUsers = emojiReactions
    .map((reaction) => {
      const user = participants.find((p) => p.id === reaction.userId);
      return {
        ...reaction,
        user,
      };
    })
    .sort((a, b) => {
      // Sort by timestamp, newest first
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });

  const formatTimestamp = (timestamp: string) => {
    const date = toJsDate(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      tabIndex={-1}
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onTouchStart={(e) => {
        //e.preventDefault();
        e.stopPropagation();
      }}
      onTouchEnd={(e) => {
        //e.preventDefault();
        e.stopPropagation();
      }}
      onTouchCancel={(e) => {
        //e.preventDefault();
        e.stopPropagation();
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden"
        tabIndex={-1}
        onPointerDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onTouchStart={(e) => {
          //e.preventDefault();
          e.stopPropagation();
        }}
        onTouchEnd={(e) => {
          //e.preventDefault();
          e.stopPropagation();
        }}
        onTouchCancel={(e) => {
          //e.preventDefault();
          e.stopPropagation();
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{emoji}</span>
            <h3 className="text-lg font-semibold text-gray-900">
              {reactionsWithUsers.length === 1
                ? ""
                : `${reactionsWithUsers.length} Reactions`}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close"
          >
            <XIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[60vh]">
          {reactionsWithUsers.map((reaction, idx) => (
            <div
              key={`${reaction.userId}-${idx}`}
              className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {reaction.user ? (
                  (() => {
                    const profileImage = useCachedImage(
                      reaction.user.profileImageUrl,
                      reaction.user.username,
                      reaction.user.id,
                    );
                    return (
                      <>
                        <img
                          src={profileImage}
                          alt={reaction.user.username}
                          className="w-10 h-10 rounded-full flex-shrink-0 object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Crect width='40' height='40' fill='%23F3F4F6'/%3E%3C/svg%3E`;
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {reaction.user.username}
                          </p>
                          <p className="text-sm text-gray-500">
                            {formatTimestamp(reaction.timestamp)}
                          </p>
                        </div>
                      </>
                    );
                  })()
                ) : (
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                      <span className="text-gray-500 text-sm">?</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-500 truncate">
                        Unknown User
                      </p>
                      <p className="text-sm text-gray-400">
                        {formatTimestamp(reaction.timestamp)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReactionDetailsModal;
